import {
  Box,
  Card,
  Chip,
  Divider,
  Drawer,
  Fade,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, {
  useState,
  useId,
  Fragment,
  useMemo,
  useTransition,
} from 'react';
import { search } from 'fast-fuzzy';

import ChevronsRightIcon from '@watershed/icons/components/ChevronsRight';
import SearchIcon from '@watershed/icons/components/Search';
import ColumnIcon from '@watershed/icons/components/Column';
import ChevronDownIcon from '@watershed/icons/components/ChevronDown';
import isNotNullish from '@watershed/shared-universal/utils/isNotNullish';

import {
  GridValidRowModel,
  gridQuickFilterValuesSelector,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridSelector,
} from './DataGrid';
import Button from '../Button';
import { TextFieldNonFormik } from '../Form/TextField';
import {
  ColumnPreset,
  DataGridDrawerProps,
  DataGridDrawerSize,
} from './DataGridTypes';
import CollapsibleSection from '../CollapsibleSection';
import { CheckboxFieldNonFormik } from '../Form/CheckboxField';
import PopoverMenu from '../PopoverMenu';
import SegmentedControl, { SegmentedControlProps } from '../SegmentedControl';

const DRAWER_SIZE_TO_WIDTH_PX: Record<DataGridDrawerSize, number> = {
  sm: 288,
  md: 360,
  lg: 480,
};
const PADDING_PX = 12;

export const DRAWER_CONTAINER_WIDTH_PX = 60;
const COLLAPSED_WIDTH_PX = 36;
const COLLAPSE_ANIMATION_DURATION_MS = 250;
const FADE_ANIMATION_DURATION_MS = COLLAPSE_ANIMATION_DURATION_MS * 1.2;

type DataGridDrawerSection<
  T extends DataGridDrawerSubsection =
    | DataGridDrawerSubsection
    | DataGridDrawerSubsectionColumns
    | DataGridDrawerSubsectionSegmentedControl,
> = {
  title?: string;
  subsections: Array<T>;
  hideWhenCollapsed?: boolean;
};

type SubsectionProps<T extends DataGridDrawerSubsection> = {
  subsection: T;
  setDrawerCollapsed: (drawerCollapsed: boolean) => void;
};

interface DataGridDrawerSubsection {
  description?: string;
  icon?: React.ReactNode;
  badgeCount?: number;
  Component: (props: SubsectionProps<any>) => JSX.Element;
}

interface DataGridDrawerSubsectionSegmentedControl
  extends DataGridDrawerSubsection {
  segmentedControlProps: SegmentedControlProps<any>;
}
function isSegmentedControlSubsection(
  subsection: DataGridDrawerSubsection
): subsection is DataGridDrawerSubsectionSegmentedControl {
  return 'segmentedControlProps' in subsection;
}

interface DataGridDrawerSubsectionColumns extends DataGridDrawerSubsection {
  columnDefs: Array<{ headerName: string | undefined; field: string }>;
  columnPresets?: Array<ColumnPreset>;
}

/**
 * DataGridDrawerContainer
 * The Drawer wrapper around the drawer contents. The drawer contents themselves
 * are overridable by passing in a custom component to the `drawer` slot.
 */
export function DataGridDrawerContainer({
  children,
}: { children: React.ReactNode }) {
  return (
    <Drawer
      open
      anchor="right"
      variant="persistent"
      sx={{
        '& .MuiDrawer-paper': {
          position: 'absolute',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          p: `${PADDING_PX}px`,
        },
      }}
    >
      {children}
    </Drawer>
  );
}

/**
 * DataGridDrawer
 * This is the default Watershed DataGrid drawer component, which allows for
 * controlling filtering, column visibility, search, and more! This component
 * can be overridden via slots.drawer if necessary.
 */
export function DataGridDrawer<R extends GridValidRowModel>({
  segmentedControlProps,
  columns = [],
  columnPresets = [],
  size = 'md',
}: DataGridDrawerProps<R>) {
  const [drawerCollapsed, setDrawerCollapsed] = useState(true);
  const handleSubsectionIconButtonClick = () => {
    setDrawerCollapsed(false);
  };
  const columnDefs = useMemo(
    () =>
      columns
        .map(({ headerName, field }) => ({ headerName, field }))
        .filter(isNotNullish),
    [columns]
  );
  const apiRef = useGridApiContext();
  const columnVisibilities = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector
  );
  const segmentedControlHasIcons = segmentedControlProps?.options.every(
    (o) => o.icon !== undefined
  );
  const columnCount = columnDefs.length;
  const hiddenColumnCount = Object.values(columnVisibilities).filter(
    (v) => v === false
  ).length;

  const sections: Array<DataGridDrawerSection> = [
    {
      subsections: [
        {
          description: 'Search',
          icon: <SearchIcon />,
          badgeCount: 0,
          Component: SearchSubsection,
        },
      ],
    },
    ...(segmentedControlProps
      ? [
          {
            subsections: [
              {
                Component: SegmentedControlSubsection,
                segmentedControlProps,
              },
            ],
            hideWhenCollapsed: !segmentedControlHasIcons,
          },
        ]
      : []),
    // TODO: Make it so that View options can be configured
    {
      title: 'View',
      subsections: [
        {
          description: 'Columns',
          icon: <ColumnIcon />,
          // If all columns are visible, no badge -- otherwise, the number of visible columns
          badgeCount:
            hiddenColumnCount === 0
              ? undefined
              : columnCount - hiddenColumnCount,
          Component: ColumnsSubsection,
          columnDefs,
          columnPresets,
        },
      ],
    },
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        width: drawerCollapsed
          ? COLLAPSED_WIDTH_PX
          : DRAWER_SIZE_TO_WIDTH_PX[size],
        transition: (theme) =>
          `all ${COLLAPSE_ANIMATION_DURATION_MS}ms ${theme.transitions.easing.easeInOut}`,
      }}
    >
      <Fade
        timeout={FADE_ANIMATION_DURATION_MS}
        in={drawerCollapsed}
        unmountOnExit
      >
        <Stack
          divider={<Divider sx={{ my: 2 }} />}
          sx={{
            width: COLLAPSED_WIDTH_PX,
            // Grid makes the animatable elements stack on top of each other
            gridRowStart: 1,
            gridColumnStart: 1,
          }}
        >
          {sections
            .filter((s) => !s.hideWhenCollapsed)
            .map((section) => (
              <Fragment key={section.title}>
                {section.subsections.map((subsection) => (
                  <SubsectionIconButton
                    key={subsection.description}
                    subsection={subsection}
                    onClick={handleSubsectionIconButtonClick}
                  />
                ))}
              </Fragment>
            ))}
        </Stack>
      </Fade>
      <Fade
        timeout={FADE_ANIMATION_DURATION_MS}
        in={!drawerCollapsed}
        unmountOnExit
      >
        <Stack
          divider={<Divider sx={{ my: 2 }} />}
          sx={{
            width: DRAWER_SIZE_TO_WIDTH_PX[size],
            // Grid makes the animatable elements stack on top of each other
            gridRowStart: 1,
            gridColumnStart: 1,
          }}
        >
          {sections.map((section) => (
            <MaybeCollapsibleSection key={section.title} title={section.title}>
              {section.subsections.map((subsection) => (
                <subsection.Component
                  key={subsection.description}
                  subsection={subsection}
                  setDrawerCollapsed={setDrawerCollapsed}
                />
              ))}
            </MaybeCollapsibleSection>
          ))}
        </Stack>
      </Fade>
    </Box>
  );
}

function MaybeCollapsibleSection({
  children,
  title,
}: { children: React.ReactNode; title?: string }) {
  if (title) {
    return (
      <CollapsibleSection titleVariant="h2" chevronOnly title={title}>
        {children}
      </CollapsibleSection>
    );
  }
  return <>{children}</>;
}

function SubsectionIconButton({
  onClick,
  subsection,
}: {
  onClick: () => void;
  subsection: DataGridDrawerSubsection;
}) {
  if (isSegmentedControlSubsection(subsection)) {
    const { options } = subsection.segmentedControlProps;
    // For display in the collapsed sidebar, we only show the icons. If there
    // are no icons to show, let's just omit the segmented control from the
    // sidebar.
    const iconOnlyOptions = options
      .filter((o) => o.icon !== undefined)
      .map((o) => ({
        ...o,
        title: undefined,
        tooltip: o.tooltip ?? String(o.title) ?? undefined,
      }));
    if (iconOnlyOptions.length === 0) {
      return null;
    }
    return (
      <SegmentedControl
        orientation="vertical"
        variant="flat"
        noPadding
        {...subsection.segmentedControlProps}
        // Re-format the options shown in the sidebar. If the option has an icon, just show the icon!
        options={iconOnlyOptions}
        buttonSx={{
          // Width is constrained in the collapsed sidebar; 7px makes the buttons align to the container
          p: '7px',
        }}
        sx={{ p: 0 }}
      />
    );
  }
  return (
    <Tooltip title={subsection.description ?? ''} placement="left">
      <Button
        onClick={onClick}
        sx={{
          p: 1,
          gap: 1,
          height: 'auto',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: '0',
        }}
      >
        {subsection.icon}
        {subsection.badgeCount ? (
          <Chip
            size="small"
            label={subsection.badgeCount}
            sx={{
              width: '100%',
              '.MuiChip-label': { p: 0 },
            }}
          />
        ) : null}
      </Button>
    </Tooltip>
  );
}

function SearchSubsection(props: SubsectionProps<DataGridDrawerSubsection>) {
  const apiRef = useGridApiContext();
  const quickFilterValues = useGridSelector(
    apiRef,
    gridQuickFilterValuesSelector
  );

  const onChangeSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    apiRef.current.setQuickFilterValues([e.currentTarget.value]);
  };
  return (
    <Stack direction="row" gap={1}>
      <TextFieldNonFormik
        id={useId()}
        placeholder="Search..."
        type="search"
        visuallyHideLabel
        sx={{ flexGrow: 1 }}
        onChange={onChangeSearchValue}
        value={quickFilterValues?.[0] || ''}
      />
      <Button
        onClick={() => props.setDrawerCollapsed(true)}
        endIcon={<ChevronsRightIcon />}
      />
    </Stack>
  );
}

function SegmentedControlSubsection(
  props: SubsectionProps<DataGridDrawerSubsectionSegmentedControl>
) {
  return (
    <SegmentedControl
      variant="flat"
      {...props.subsection.segmentedControlProps}
    />
  );
}

function ColumnsSubsection(
  props: SubsectionProps<DataGridDrawerSubsectionColumns>
) {
  const { columnDefs } = props.subsection;
  const columnPresets = props.subsection.columnPresets ?? [];
  const [searchColumnsValue, setSearchColumnsValue] = useState('');
  const [_, searchTransition] = useTransition();
  const apiRef = useGridApiContext();
  const columnVisibilities = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector
  );
  const visibleColumnDefs = useMemo(() => {
    if (!searchColumnsValue) {
      return columnDefs;
    }
    return search(searchColumnsValue, columnDefs, {
      keySelector: (colDef) => colDef.field + colDef.headerName,
    });
  }, [columnDefs, searchColumnsValue]);

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">Columns</Typography>
        {columnPresets.length > 0 && (
          <PopoverMenu
            trigger={
              <Button
                variant="text"
                endIcon={<ChevronDownIcon color="text.secondary" size={16} />}
                size="small"
                sx={{ color: 'text.secondary', fontSize: 14 }}
              >
                Presets
              </Button>
            }
            options={columnPresets.map((p) => ({
              title: p.presetName,
              onClick: () => {
                apiRef.current.setColumnVisibilityModel(
                  columnDefs.reduce(
                    (acc, { field }) => ({
                      ...acc,
                      [field]: p.presetColumns.includes(field),
                    }),
                    {}
                  )
                );
              },
            }))}
          />
        )}
      </Stack>
      <Card variant="outlined" sx={{ p: 1 }}>
        <Stack direction="row" alignItems="center">
          <CheckboxFieldNonFormik
            checked={Object.values(columnVisibilities).every(
              (v) => v !== false
            )}
            inputId="columns-select-all"
            onChange={(e) => {
              if (e.currentTarget.checked) {
                apiRef.current.setColumnVisibilityModel({}); // Default is all visible
              } else {
                apiRef.current.setColumnVisibilityModel(
                  columnDefs.reduce(
                    (acc, { field }) => ({ ...acc, [field]: false }),
                    {}
                  )
                );
              }
            }}
          />
          <Typography
            component="label"
            htmlFor="columns-select-all"
            variant="body2"
          >
            Select all ({columnDefs.length})
          </Typography>
        </Stack>
        <Divider sx={{ mt: 1.5, mb: 1 }} />
        <Stack gap={2}>
          <TextFieldNonFormik
            id={useId()}
            placeholder="Search columns…"
            type="search"
            visuallyHideLabel
            sx={{ flexGrow: 1 }}
            onChange={(e) => {
              searchTransition(() => {
                setSearchColumnsValue(e.currentTarget.value);
              });
            }}
            value={searchColumnsValue}
          />
          {visibleColumnDefs.map(({ headerName, field }) => (
            <Stack direction="row" alignItems="center" key={field}>
              <CheckboxFieldNonFormik
                checked={columnVisibilities[field] !== false}
                inputId={field}
                onChange={(e) => {
                  apiRef.current.setColumnVisibility(
                    field,
                    e.currentTarget.checked
                  );
                }}
                sx={{ flexShrink: 0 }}
              />
              <Typography
                component="label"
                htmlFor={field}
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {headerName ?? field}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}
